import { useId } from 'react';
import { STATE_LABEL, STATE_ORDER } from './states';
import type { Lot, Member, QrFilters } from './types';

export interface QrFiltersBarProps {
  value: QrFilters;
  onChange: (next: QrFilters) => void;
  lots?: Lot[];
  custodians?: Member[];
  showKind?: boolean;
  searchPlaceholder?: string;
}

const selectCls = 'rounded-sm border border-line bg-white px-2 py-1.5 text-sm text-ink';

export function QrFiltersBar({ value, onChange, lots, custodians, showKind = true, searchPlaceholder = 'Buscar por negocio, código o referencia' }: QrFiltersBarProps) {
  const id = useId();
  const set = (patch: Partial<QrFilters>) => onChange({ ...value, ...patch });
  return (
    <div className="flex flex-wrap items-center gap-2" role="search">
      <label htmlFor={`${id}-q`} className="sr-only">
        Buscar
      </label>
      <input
        id={`${id}-q`}
        type="search"
        value={value.q ?? ''}
        onChange={(e) => set({ q: e.target.value })}
        placeholder={searchPlaceholder}
        className="w-72 max-w-full rounded-sm border border-line bg-white px-3 py-1.5 text-sm placeholder:text-gray-500"
      />
      <label htmlFor={`${id}-state`} className="sr-only">
        Estado
      </label>
      <select id={`${id}-state`} className={selectCls} value={value.state ?? ''} onChange={(e) => set({ state: e.target.value as QrFilters['state'] })}>
        <option value="">Todos los estados</option>
        {STATE_ORDER.map((s) => (
          <option key={s} value={s}>
            {STATE_LABEL[s]}
          </option>
        ))}
      </select>
      {showKind && (
        <>
          <label htmlFor={`${id}-kind`} className="sr-only">
            Tipo
          </label>
          <select id={`${id}-kind`} className={selectCls} value={value.kind ?? ''} onChange={(e) => set({ kind: e.target.value as QrFilters['kind'] })}>
            <option value="">Todos los tipos</option>
            <option value="physical">Físico (lote)</option>
            <option value="digital">Digital (clásico, con estilo o QR Art)</option>
            <option value="legacy">Integración</option>
          </select>
        </>
      )}
      {lots && lots.length > 0 && (
        <>
          <label htmlFor={`${id}-lot`} className="sr-only">
            Lote
          </label>
          <select id={`${id}-lot`} className={selectCls} value={value.lotId ?? ''} onChange={(e) => set({ lotId: e.target.value })}>
            <option value="">Todos los lotes</option>
            {lots.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name ? `${l.id} · ${l.name}` : l.id}
              </option>
            ))}
          </select>
        </>
      )}
      {custodians && custodians.length > 0 && (
        <>
          <label htmlFor={`${id}-cust`} className="sr-only">
            Custodio
          </label>
          <select id={`${id}-cust`} className={selectCls} value={value.custodianUid ?? ''} onChange={(e) => set({ custodianUid: e.target.value })}>
            <option value="">Todos los custodios</option>
            {custodians.map((m) => (
              <option key={m.uid} value={m.uid}>
                {m.name ?? m.email ?? m.uid}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}
